import React, { useEffect, useRef } from 'react';
import store from 'store2';
import { useNavigate } from 'react-router-dom';
import { AppointmentDispatchTypes, useAppointmentDispatch } from '../../context/Appointment/appointment';
import { useKioskState, useKioskDispatch, usePossibleDealers, useTowInSupport } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { ThemeImage } from '../../controls/ThemeImage/ThemeImage';
import { formatAsPhoneNumber } from '../../helpers/Number';
import { Header } from '../Layout/Header';
import { default as css } from './HomeKiosk.module.scss';
import { GMGenericButton } from '../../controls/ButtonBase/index';
import classNames from 'classnames';
import { AppRoutePaths, Customer_AddRoutesPaths, CustomerRoutePaths, PathName } from '../../helpers/Routes';
import { KioskDispatchTypes } from '../../context/Kiosk/kiosk.types';
import { PageProvider } from '../../context/Page/page';
import { GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';

export const HomeKiosk: React.FC = () => {
    const Localization = useLocalization();
    const { dealer, kioskType, keyPickupOption } = useKioskState();
    const appointmentDispatch = useAppointmentDispatch();
    const kioskDispatch = useKioskDispatch();
    const possibleDealers = usePossibleDealers();
    const navigate = useNavigate();
    const towInSupport = useTowInSupport();

    useEffect(() => {
        // Always remove the appointment
        appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
        possibleDealers && kioskDispatch({ type: KioskDispatchTypes.ClearPossibleDealers });
        store.session.remove('tempResult');
        store.session.remove('tempAppointment');
    });

    const parentRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
    const originalFontSize = '28px';

    // handle button heading resizing on language change to avoid overflowing text
    parentRefs.forEach((parentRef) => {
        const handleResize = () => {
            if (parentRef?.current) {
                const parentWidth: number = parseFloat(window.getComputedStyle(parentRef.current).width);
                const divElement: HTMLDivElement = parentRef.current.getElementsByTagName('div')[0];

                divElement.style.fontSize = originalFontSize;
                while (divElement.scrollWidth > parentWidth) {
                    divElement.style.fontSize = `${parseFloat(window.getComputedStyle(divElement).fontSize) - 0.5}px`;
                }
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    });

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, []);

    const homeButtonList = [];
    if (dealer.newAppointmentEnabled || dealer.oldAppointmentEnabled) {
        homeButtonList.push(
            <div
                key={'checkin'}
                data-testid={'checkin'}
                className={css.buttonWrapper}
            >
                <GMGenericButton
                    onClick={() => navigate(AppRoutePaths.Landing + AppRoutePaths.Appointment)}
                    title={Localization.ClickToBeginCheckIn}
                    id={Localization.ClickToBeginCheckIn}
                    ariaLabel={Localization.ClickToBeginCheckIn}
                >
                    <div className={css.buttonContent}>
                        <div>
                            <div className={css.leftIcon}>
                                <GMRoundedIcon
                                    themeType={'primary'}
                                    roundedBackgroundSize={'small'}
                                    landingPageStyle
                                >
                                    <GMSizedIcon iconType={'Check-In'} iconSize={'large'} />
                                </GMRoundedIcon>
                            </div>
                            <div className={css.buttonText} ref={parentRefs[0]}>
                                <div className={css.heading}>{Localization.CheckIn}</div>
                                <div className={css.mainContent}>{Localization.WelcomeTapToStartCheckingIn}</div>
                            </div>
                        </div>
                        <div className={css.arrowIcon}>
                            <GMSizedIcon iconType={'Arrow'} iconSize={'medium'} />
                        </div>
                    </div>
                </GMGenericButton>
            </div>,
        );
    }
    if (towInSupport) {
        homeButtonList.push(
            <div
                key={'tow-dropoff'}
                data-testid={'tow-dropoff'}
                className={css.buttonWrapper}
            >
                <GMGenericButton
                    onClick={() => navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Add, Customer_AddRoutesPaths.TowCompanyDetails))}
                    title={Localization.TowCompaniesTapToGetStarted}
                    id={Localization.TowCompaniesTapToGetStarted}
                    ariaLabel={Localization.TowCompaniesTapToGetStarted}
                >
                    <div className={css.buttonContent}>
                        <div>
                            <div className={css.leftIcon}>
                                <GMRoundedIcon
                                    themeType={'primary'}
                                    roundedBackgroundSize={'small'}
                                    landingPageStyle
                                >
                                    <GMSizedIcon iconType={'Tow-Truck'} iconSize={'large'} />
                                </GMRoundedIcon>
                            </div>
                            <div className={css.buttonText} ref={parentRefs[1]}>
                                <div className={css.heading}>{Localization.TowIn}</div>
                                <div className={css.mainContent}>{Localization.TowCompaniesTapToGetStarted}</div>
                            </div>
                        </div>
                        <div className={css.arrowIcon}>
                            <GMSizedIcon iconType={'Arrow'} iconSize={'medium'} />
                        </div>
                    </div>
                </GMGenericButton>
            </div>,
        );
    }
    if ((kioskType === 'ssk' || kioskType === 'express') && keyPickupOption) {
        homeButtonList.push(
            <div
                key={'pickup'}
                data-testid={'pickup'}
                className={css.buttonWrapper}
            >
                <GMGenericButton
                    onClick={() => navigate(AppRoutePaths.VehiclePickUp)}
                    title={Localization.TapHereToGetStartedWithVehiclePickup}
                    id={Localization.TapHereToGetStartedWithVehiclePickup}
                    ariaLabel={Localization.TapHereToGetStartedWithVehiclePickup}
                >
                    <div className={css.buttonContent}>
                        <div>
                            <div className={css.leftIcon}>
                                <GMRoundedIcon
                                    themeType={'primary'}
                                    roundedBackgroundSize={'small'}
                                    landingPageStyle
                                >
                                    <GMSizedIcon iconType={'Vehicle'} iconSize={'large'} />
                                </GMRoundedIcon>
                            </div>
                            <div className={css.buttonText} ref={parentRefs[2]}>
                                <div className={css.heading}>{Localization.PickUp}</div>
                                <div className={css.mainContent}>{Localization.YourRideAwaitsTapToCompleteYourVehiclePickUp}</div>
                            </div>
                        </div>
                        <div className={css.arrowIcon}>
                            <GMSizedIcon iconType={'Arrow'} iconSize={'medium'} />
                        </div>
                    </div>
                </GMGenericButton>
            </div>,
        );
    }

    return (
        <div data-testid={'HomeKiosk'} className={css.landingPage}>
            <PageProvider suppressTimeout={true}>
                <Header />
            </PageProvider>
            <div className={css.expressCheckIn}>
                <div className={css.dealerInformation}>
                    <div className={css.dealerLogo}>
                        <ThemeImage themeImageType={'logo'} />
                    </div>
                    <h2 className={css.dealerName}>{dealer.name}</h2>
                    <h3 className={css.dealerAddress}>
                        <div>{dealer.street}</div>
                        <div>
                            {dealer.city}{', '}{dealer.stateOrProvince} {dealer.postalCode}
                        </div>
                    </h3>
                    <h4 className={css.dealerNumber}>
                        <span>{formatAsPhoneNumber(dealer ? dealer.phone : '')}</span>
                    </h4>
                </div>
                <div
                    className={classNames(css.buttonContainer, homeButtonList.length === 2 ? css.twoChildren : '')}
                    style={{ gridTemplateColumns: 'repeat(' + homeButtonList.length + ', 1fr)' }}
                >
                    {homeButtonList}
                </div>
            </div>
            <div className={css.copyrightNotice}>
                <div>{Localization.formatString(Localization.GoMotoCopyright, new Date()
                    .getFullYear())}
                </div>
                {kioskType === 'ssk' && <div>{Localization.GoMotoPatent}</div>}
            </div>
            <div className={css.contentBackground}>
                <ThemeImage themeImageType={'landingPageBackground'} />
            </div>
        </div>
    );
};

export { HomeKiosk as default };
